/* Model personality — Message style card (bottom of Conversational style):
   start letter, speed, bubbles per message and words per bubble. */

import { useTranslation } from 'react-i18next';
import { messageStyleErrors, type MessageStyleDraft } from '../../messageStylePolicy';
import { Card, Row, Section, Seg } from '../kit';
import { C } from '../tokens';
import { font as f } from '../style';

function Num({ value, onChange, bad, label }: { value: string; onChange: (v: string) => void; bad: boolean; label: string }) {
  return (
    <input
      value={value}
      aria-label={label}
      aria-invalid={bad}
      inputMode="numeric"
      onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      style={{ width: 52, height: 28, boxSizing: 'border-box', background: C.input, border: `1px solid ${bad ? C.purplePale : C.border}`, borderRadius: 6, padding: '0 8px', color: C.text, font: f(500, 12.5), textAlign: 'center', outline: 'none' }}
    />
  );
}

export function MessageStyleCard({ value, onChange }: { value: MessageStyleDraft; onChange: (v: MessageStyleDraft) => void }) {
  const { t } = useTranslation('assistantCompact');
  const err = messageStyleErrors(value);
  const set = (p: Partial<MessageStyleDraft>) => onChange({ ...value, ...p });
  const bubErr = err.bubble_count_min ?? err.bubble_count_max;
  const bubHelp = bubErr === 'rangeError' ? t('mp.ms.bubRange') : bubErr ? t('mp.ms.bubBad') : t('mp.ms.bubHelp');
  const words = err.bubble_word_limit;
  return (
    <Section title={t('mp.ms.title')} hint={t('mp.ms.hint')}>
      <Card>
        <Row label={t('mp.ms.cap')} help={t(`mp.ms.capHelp.${value.start_letter_policy}`)}>
          <Seg
            value={value.start_letter_policy}
            onChange={(v) => set({ start_letter_policy: v })}
            options={[{ id: 'capital', label: 'Aa' }, { id: 'small', label: 'aa' }, { id: 'mixed', label: 'Aa / aa' }]}
          />
        </Row>
        <Row label={t('mp.ms.speed')} help={t(`mp.ms.speedHelp.${value.speed_policy}`)}>
          <Seg
            value={value.speed_policy}
            onChange={(v) => set({ speed_policy: v })}
            options={(['quick', 'balanced', 'slow'] as const).map((id) => ({ id, label: t(`mp.ms.speedOpt.${id}`) }))}
          />
        </Row>
        <Row label={t('mp.ms.bub')} help={bubHelp} helpColor={bubErr ? C.purplePale : undefined}>
          <Num label={t('mp.ms.min')} value={value.bubble_count_min} bad={!!err.bubble_count_min || err.bubble_count_max === 'rangeError'} onChange={(v) => set({ bubble_count_min: v })} />
          <span>{t('to')}</span>
          <Num label={t('mp.ms.max')} value={value.bubble_count_max} bad={!!err.bubble_count_max} onChange={(v) => set({ bubble_count_max: v })} />
          <span>{t('mp.ms.bubbles')}</span>
        </Row>
        <Row label={t('mp.ms.words')} help={words ? t('mp.ms.wordsBad') : t('mp.ms.wordsHelp')} helpColor={words ? C.purplePale : undefined}>
          <Num label={t('mp.ms.words')} value={value.bubble_word_limit} bad={!!words} onChange={(v) => set({ bubble_word_limit: v })} />
          <span>{t('mp.ms.maxWords')}</span>
        </Row>
      </Card>
    </Section>
  );
}
